import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactUiMouseEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Package,
  PauseCircle,
  RefreshCw,
} from "lucide-react";

import { BatchOnHoldReasonCallout, productionCalendarBatchAnchorKey } from "@/components/manufacturing/BatchOnHoldReasonCallout";
import { BatchTeamNotesGlyph } from "@/components/manufacturing/BatchTeamNotesGlyph";
import type { BatchTeamNotesPreview } from "@/components/manufacturing/BatchTeamNotesPanel";
import { SkuSearchableSelect } from "@/components/mrp/SkuSearchableSelect";
import { EntityPeekHint } from "@/components/hub/EntityPeekHint";
import { CreateAlternateOrderModal } from "@/pages/manufacturing/CreateAlternateOrderModal";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { HoverHintContent, HoverHintTrigger, HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { useQcIncidentModal } from "@/lib/qc/qcIncidentModalContext";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { mergeProductionCalendarBatchPatch } from "@/lib/productionCalendarBatchMerge";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import {
  alternateBoardAvailabilityForBatch,
  type AlternateBoardAvailability,
  type ManufacturingAlternatesBoardApiResponse,
} from "@/lib/manufacturingAlternatesForCalendar";
import {
  buildManufacturingBatchDetailsPath,
  buildManufacturingEditBatchPathForBatch,
} from "@/lib/manufacturingEditBatchPath";
import { cn } from "@/lib/utils";
import {
  compareSgsProductionCalendarStationOrder,
  SGS_PACK_OUT_STATION_ID,
  SGS_QUALITY_CONTROL_STATION_ID,
} from "@/lib/sgsProductionCalendarStationOrder";
import {
  computePointAnchoredContextMenu,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { MANUFACTURING_HUB_READ_ONLY } from "@/pages/manufacturing/workOrdersPageModel";
import { formatSentToPackOutAttributionSummary } from "@/pages/manufacturing/sentToPackOutAttribution";

export interface ProductionCalendarStation {
  id: number;
  name: string;
  locationId: number | null;
  isTerminal: boolean;
}

interface ProductionCalendarShipmentLine {
  sku: string;
  quantity: number;
}

interface ProductionCalendarOutboundBlockedUnit {
  serialFciId: number;
  finishedSku: string;
  clampSerial: string | null;
  pcbaSerial: string | null;
  scrap: boolean;
  qualityIncidentId: number | null;
  qualityIncidentIssue: string | null;
}

interface ProductionCalendarShipmentSummary {
  shipmentId: number;
  sentToPackOut: boolean;
  dateShipped: string | null;
  sentToPackOutAt: string | null;
  sentToPackOutByUserId: string | null;
  sentToPackOutByLabel: string | null;
  sentToPackOutReason: string | null;
  composition: ProductionCalendarShipmentLine[];
  outboundBlockedUnits: ProductionCalendarOutboundBlockedUnit[];
}

export interface ProductionCalendarBatch {
  batchId: number;
  mfrShipByDate: string | null;
  locationId: number | null;
  locationName: string;
  manufacturerSalesOrderNumber: string | null;
  /** Lowest linked sales order id from batch items; null when no sales-order lines on the batch. */
  primarySalesOrderId: number | null;
  /** Open sales order at the same ship-to as the batch; edit-batch anchor when there are no batch items. */
  anchorSalesOrderId: number | null;
  /** Linked GSO context rows (one per distinct sales order on batch lines, or anchor when no lines). */
  salesOrderContexts: ProductionCalendarSalesOrderContext[];
  /** Sum of `batch_item.quantity` for lines whose `public.sku.serialize` is true (serialize-only rollup). */
  plannedSerializedDevices: number;
  /** Sum of every non-empty batch line SKU quantity (serialize and non-serialize). Shipped % denominator. */
  plannedBatchLineDevices: number;
  /** Per trimmed SKU rollup from `batch_item` quantities; `serialize` from `public.sku`; `divisor` from `sku_divisors` (default 1). */
  batchLinePlans: { sku: string; plannedQuantity: number; serialize: boolean; divisor?: number }[];
  /** Shipped `serial_fci` rows grouped by batch line SKU and line station label. */
  shippedDetailBySkuStation: { sku: string; stationName: string; count: number }[];
  /** WIP counts per `station_id` split by batch line SKU (for station row hover hints). */
  stationSkuBreakdown: {
    stationId: number | null;
    sku: string;
    count: number;
    divisor?: number;
    clampUnits?: number;
  }[];
  /** Non-serialized quantities on batch shipments (`shipment_item_unserialized`), any ship state. */
  unserializedOnShipmentBySku: { sku: string; quantity: number }[];
  /** Bulk on open shipments without carton (`carton_number` null). */
  unserializedQcOpenBySku: { sku: string; quantity: number; clampUnits?: number }[];
  unserializedQcOpenQty: number;
  /** Sum of open QC bulk in clamp units when `sku_divisors` apply (matches station row rounding). */
  unserializedQcOpenClamp?: number;
  /** Bulk on open shipments with carton; not shipped. */
  unserializedPackOutOpenBySku: { sku: string; quantity: number; clampUnits?: number }[];
  unserializedPackOutOpenQty: number;
  unserializedPackOutOpenClamp?: number;
  /** Distinct line SKUs from `batch_item` on this batch (all lines; no `public.sku` join). */
  finishedSkus: string[];
  stationCounts: Record<string, number>;
  /** Clamp-unit counts per `station_id` (from `sku_divisors`); when present, preferred for calendar station rows. */
  stationCountsDisplay?: Record<string, number>;
  unassignedCount: number;
  trackedTotal: number;
  completedCount: number;
  teamNoteCount: number;
  latestTeamNote: BatchTeamNotesPreview | null;
  shipmentOnHold?: boolean;
  shipments?: ProductionCalendarShipmentSummary[];
}

interface ProductionCalendarResponse {
  timeZone: string;
  startDate: string;
  endDate: string;
  weeks: number;
  stations: ProductionCalendarStation[];
  batches: ProductionCalendarBatch[];
  overdueBatches: ProductionCalendarBatch[];
  searchResults?: ProductionCalendarBatch[];
  searchSku?: string | null;
  searchResultsTruncated?: boolean;
  shippedLastWeek: {
    startDate: string;
    endDate: string;
    shippedCount: number;
  };
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
}

function productionCalendarHubCacheChannel(
  startDate: string,
  weeks: number,
  includeOverdue: boolean,
  searchSku: string,
): string {
  const viewKey = `${startDate}|${weeks}|${includeOverdue ? "1" : "0"}|${searchSku.trim()}`;
  return HUB_CACHE_CHANNEL.manufacturingProductionCalendar(viewKey);
}

async function fetchProductionCalendar(
  startDate: string,
  weeks: number,
  includeOverdue: boolean,
  searchSku: string,
  options?: { background?: boolean; refresh?: boolean },
): Promise<ProductionCalendarResponse> {
  const trimmedSku = searchSku.trim();
  return apiFetch<ProductionCalendarResponse>("/api/manufacturing/production-calendar", {
    query: {
      startDate,
      weeks,
      ...(includeOverdue ? {} : { includeOverdue: "0" }),
      ...(trimmedSku !== "" ? { sku: trimmedSku } : {}),
      ...(options?.background ? { background: "1" } : {}),
      ...(options?.refresh ? { refresh: "1" } : {}),
    },
  });
}

const DEFAULT_WEEKS = 6;

interface ProductionCalendarSalesOrderContext {
  salesOrderNumber: string | null;
  clientName: string | null;
  endUserName: string | null;
  sgsShipByDate: string | null;
}

/** Hover or keyboard-focus the question mark to show help text (portaled hover hint). */
function MetricCardHelp({
  hint,
  ariaLabel = "Explain this metric",
  hoverHintAlign = "center",
}: {
  hint: ReactNode;
  ariaLabel?: string;
  hoverHintAlign?: "center" | "start";
}) {
  return (
    <HoverHintTrigger
      align={hoverHintAlign}
      className="ml-0.5 align-middle"
      trigger={
        <span
          tabIndex={0}
          className="inline-flex h-4 w-4 shrink-0 cursor-help select-none items-center justify-center rounded-full border border-slate-300 bg-slate-50 text-[10px] font-bold leading-none text-slate-500 outline-none ring-brand-500 focus-visible:ring-2"
          aria-label={ariaLabel}
        >
          ?
        </span>
      }
    >
      {hint}
    </HoverHintTrigger>
  );
}

function batchHeadline(batch: ProductionCalendarBatch): string {
  return `Batch ${batch.batchId}`;
}

function batchSalesOrderContextAriaSummary(batch: ProductionCalendarBatch): string {
  const gsoNumbers = batch.salesOrderContexts
    .map((context) => context.salesOrderNumber?.trim())
    .filter((value): value is string => value != null && value !== "");
  if (gsoNumbers.length === 0) {
    return "No linked sales order context";
  }
  if (gsoNumbers.length === 1) {
    const context = batch.salesOrderContexts[0];
    const parts = [gsoNumbers[0]];
    if (context?.clientName?.trim()) parts.push(context.clientName.trim());
    if (context?.endUserName?.trim()) parts.push(context.endUserName.trim());
    return parts.join(" · ");
  }
  return `${gsoNumbers.length} linked GSOs: ${gsoNumbers.join(", ")}`;
}

function batchHasSalesOrderContext(batch: ProductionCalendarBatch): boolean {
  return batch.salesOrderContexts.length > 0;
}

function batchSalesOrderContextHoverHint(batch: ProductionCalendarBatch): ReactNode {
  return <EntityPeekHint kind="sales_order_list" contexts={batch.salesOrderContexts} />;
}

/** Distinct batch line SKUs for alternate-order prefill (bulk lines first, same order as the card subtitle). */
export function batchAlternateOrderSkuOptions(batch: ProductionCalendarBatch): string[] {
  return subtitleSkuOrder(batch);
}

/** Card subtitle shows at most two SKUs; bulk (`serialize === false`) lines are listed first so they are not crowded out by alphabetical serialized SKUs. */
function subtitleSkuOrder(batch: ProductionCalendarBatch): string[] {
  const plans = batch.batchLinePlans;
  if (plans.length === 0) return [...batch.finishedSkus];

  const seen = new Set<string>();
  const ordered: string[] = [];

  for (const plan of plans) {
    if (plan.serialize !== false) continue;
    if (seen.has(plan.sku)) continue;
    seen.add(plan.sku);
    ordered.push(plan.sku);
  }
  for (const plan of plans) {
    if (plan.serialize !== true) continue;
    if (seen.has(plan.sku)) continue;
    seen.add(plan.sku);
    ordered.push(plan.sku);
  }
  for (const sku of batch.finishedSkus) {
    if (seen.has(sku)) continue;
    seen.add(sku);
    ordered.push(sku);
  }

  return ordered;
}

function formatBatchSubtitleLines(skusList: string[]): string {
  const skus = skusList.slice(0, 2).join(", ");
  if (skusList.length > 2) {
    return `${skus} +${skusList.length - 2} more`;
  }
  return skus.length > 0 ? skus : "—";
}

function batchSubtitleAriaLabel(batch: ProductionCalendarBatch): string | undefined {
  const plans = batch.batchLinePlans;
  if (plans.length > 0) {
    return [...plans]
      .sort((left, right) => left.sku.localeCompare(right.sku))
      .map((plan) => `${plan.plannedQuantity} ${plan.sku}`)
      .join(", ");
  }
  const skus = subtitleSkuOrder(batch);
  return skus.length > 0 ? skus.join(", ") : undefined;
}

function batchLineQuantitiesHoverHint(batch: ProductionCalendarBatch): ReactNode {
  const plans = [...batch.batchLinePlans].sort((left, right) => left.sku.localeCompare(right.sku));
  if (plans.length === 0) {
    const skus = batch.finishedSkus;
    if (skus.length === 0) {
      return <span className="text-slate-500">No batch line SKUs.</span>;
    }
    return (
      <div className="max-w-[20rem] space-y-2 whitespace-normal text-left">
        <HoverHintDefinitionGrid
          rows={skus.map((sku) => ({
            label: "SKU",
            value: sku,
          }))}
        />
        <p className="text-[10px] text-slate-500">Planned quantities are not on this payload.</p>
      </div>
    );
  }
  return (
    <HoverHintDefinitionGrid
      rows={plans.map((plan) => ({
        label: plan.sku,
        value: <span className="tabular-nums">{plan.plannedQuantity.toLocaleString()}</span>,
      }))}
    />
  );
}

/** Denominator for shipped % / “X/Y shipped”: sum of all batch line quantities when available, otherwise serialize rollup. */
function displayPlannedDenom(batch: ProductionCalendarBatch): number {
  const lineTotal = batch.plannedBatchLineDevices ?? 0;
  if (lineTotal > 0) return lineTotal;
  return batch.plannedSerializedDevices ?? 0;
}

function divisorForBatchLineSku(batch: ProductionCalendarBatch, sku: string): number {
  const plan = batch.batchLinePlans.find((row) => row.sku === sku);
  const raw = plan?.divisor;
  return raw != null && raw >= 1 ? raw : 1;
}

/** Shipped `serial_fci` rows credited to SKU, scaled by **`batchLinePlans[].divisor`** (same clamp idea as **`stationCountsDisplay`**). */
function shippedCountForSku(batch: ProductionCalendarBatch, sku: string): number {
  let serialRows = 0;
  for (const row of batch.shippedDetailBySkuStation) {
    if (row.sku === sku) serialRows += row.count;
  }
  return serialRows / divisorForBatchLineSku(batch, sku);
}

function stationSkuBreakdownLines(
  breakdown: {
    stationId: number | null;
    sku: string;
    count: number;
    clampUnits?: number;
  }[],
  stationId: number | null,
): { sku: string; count: number }[] {
  const filtered = breakdown.filter((row) => {
    if (stationId === null) return row.stationId === null;
    return row.stationId === stationId;
  });
  return filtered
    .map(({ sku, count, clampUnits }) => ({
      sku,
      count: clampUnits != null && Number.isFinite(clampUnits) ? clampUnits : count,
    }))
    .sort((left, right) => left.sku.localeCompare(right.sku));
}

/** Serials counted only when `station_id` is set (on the floor / in a named station), not null. */
function countSerialsAtStationForSku(
  breakdown: { stationId: number | null; sku: string; count: number }[],
  sku: string,
): number {
  let sum = 0;
  for (const row of breakdown) {
    if (row.sku !== sku) continue;
    if (row.stationId != null) sum += row.count;
  }
  return sum;
}

function unserializedQtyOnShipment(batch: ProductionCalendarBatch, sku: string): number {
  for (const row of batch.unserializedOnShipmentBySku ?? []) {
    if (row.sku === sku) return row.quantity;
  }
  return 0;
}

/** Serialized: `serial_fci` with non-null `station_id` (serial row count). Non-serialized: quantity on batch `shipment_item_unserialized`. */
function progressedOutOfNotStartedForLine(
  batch: ProductionCalendarBatch,
  plan: { sku: string; plannedQuantity: number; serialize: boolean },
): number {
  if (plan.serialize === false) {
    return unserializedQtyOnShipment(batch, plan.sku);
  }
  return countSerialsAtStationForSku(batch.stationSkuBreakdown ?? [], plan.sku);
}

function planDivisor(plan: { divisor?: number }): number {
  const raw = plan.divisor;
  return raw != null && raw >= 1 ? raw : 1;
}

/**
 * Per batch line: planned minus units already in manufacturing flow.
 * Serialized kit lines: `plannedQuantity` is kits from `batch_item`; `progressed` is serial rows at stations —
 * compare in kit space (`plannedQuantity - serials/divisor`). Bulk lines: subtract shipment quantities in batch line units.
 * Lines returned here use **manufacturing clamp units** (kits) for serialized SKU rows; bulk rows use device/unserialized units
 * then {@link notStartedLinesClampUnitsFromDevices} applies divisor only where needed for display.
 */
function notStartedHoverHintLinesBySku(batch: ProductionCalendarBatch): { sku: string; count: number }[] {
  const breakdown = batch.stationSkuBreakdown ?? [];
  const map = new Map<string, number>();
  const plannedSkus = new Set(batch.batchLinePlans.map((plan) => plan.sku));
  for (const plan of batch.batchLinePlans) {
    const progressed = progressedOutOfNotStartedForLine(batch, plan);
    let notAtStation: number;
    if (plan.serialize === true) {
      const divisor = planDivisor(plan);
      const progressedKits = progressed / divisor;
      notAtStation = Math.max(0, plan.plannedQuantity - progressedKits);
    } else {
      notAtStation = Math.max(0, plan.plannedQuantity - progressed);
    }
    if (notAtStation > 0) map.set(plan.sku, notAtStation);
  }
  for (const row of breakdown) {
    if (row.stationId != null) continue;
    if (plannedSkus.has(row.sku)) continue;
    if (row.count <= 0) continue;
    const add =
      row.clampUnits != null && Number.isFinite(row.clampUnits) ? row.clampUnits : row.count;
    map.set(row.sku, (map.get(row.sku) ?? 0) + add);
  }
  if (map.size === 0 && batch.batchLinePlans.length === 0) {
    const denom = displayPlannedDenom(batch);
    if (denom > 0 && batch.finishedSkus.length === 1) {
      const onlySku = batch.finishedSkus[0] ?? "";
      if (onlySku !== "") {
        const bulk = unserializedQtyOnShipment(batch, onlySku);
        const serial = countSerialsAtStationForSku(breakdown, onlySku);
        const progressed = Math.max(bulk, serial);
        const notAtStation = Math.max(0, denom - progressed);
        if (notAtStation > 0) map.set(onlySku, notAtStation);
      }
    }
  }
  return [...map.entries()]
    .map(([sku, count]) => ({ sku, count }))
    .sort((left, right) => left.sku.localeCompare(right.sku));
}

/** Serialized planned-not-started is already in kit / clamp units; bulk remainder stays in batch line units (often devices). */
function notStartedLinesClampUnitsFromDevices(
  batch: ProductionCalendarBatch,
  deviceLines: { sku: string; count: number }[],
): { sku: string; count: number }[] {
  const planBySku = new Map(batch.batchLinePlans.map((plan) => [plan.sku, plan]));
  return deviceLines.map(({ sku, count }) => {
    const plan = planBySku.get(sku);
    if (plan?.serialize === true) {
      return { sku, count };
    }
    const divisor = planDivisor(plan ?? {});
    return { sku, count: divisor > 1 ? count / divisor : count };
  });
}

function bulkUnserializedHoverHintLines(
  rows: { sku: string; quantity: number; clampUnits?: number }[] | undefined,
): { sku: string; count: number }[] {
  return (rows ?? [])
    .map((row) => ({
      sku: row.sku,
      count: row.clampUnits != null && Number.isFinite(row.clampUnits) ? row.clampUnits : row.quantity,
    }))
    .sort((left, right) => left.sku.localeCompare(right.sku));
}

function mergeSkuCountLines(
  first: { sku: string; count: number }[],
  second: { sku: string; count: number }[],
): { sku: string; count: number }[] {
  const map = new Map<string, number>();
  for (const row of first) {
    map.set(row.sku, (map.get(row.sku) ?? 0) + row.count);
  }
  for (const row of second) {
    map.set(row.sku, (map.get(row.sku) ?? 0) + row.count);
  }
  return [...map.entries()]
    .map(([sku, count]) => ({ sku, count }))
    .filter((row) => row.count > 0)
    .sort((left, right) => left.sku.localeCompare(right.sku));
}

/** `station_counts` plus open-shipment bulk in clamp units (QC / Pack out) for row labels. */
function stationCountsWithOpenBulk(batch: ProductionCalendarBatch): Record<string, number> {
  const hasDisplay =
    batch.stationCountsDisplay != null && Object.keys(batch.stationCountsDisplay).length > 0;
  const out: Record<string, number> = { ...(hasDisplay ? batch.stationCountsDisplay : batch.stationCounts) };
  const qc = batch.unserializedQcOpenClamp ?? batch.unserializedQcOpenQty ?? 0;
  const packOut = batch.unserializedPackOutOpenClamp ?? batch.unserializedPackOutOpenQty ?? 0;
  if (qc > 0) {
    const key = String(SGS_QUALITY_CONTROL_STATION_ID);
    out[key] = (out[key] ?? 0) + qc;
  }
  if (packOut > 0) {
    const key = String(SGS_PACK_OUT_STATION_ID);
    out[key] = (out[key] ?? 0) + packOut;
  }
  return out;
}

function stationRowHoverHintLines(batch: ProductionCalendarBatch, stationId: number): { sku: string; count: number }[] {
  const serialLines = stationSkuBreakdownLines(batch.stationSkuBreakdown ?? [], stationId);
  if (stationId === SGS_QUALITY_CONTROL_STATION_ID) {
    return mergeSkuCountLines(serialLines, bulkUnserializedHoverHintLines(batch.unserializedQcOpenBySku));
  }
  if (stationId === SGS_PACK_OUT_STATION_ID) {
    return mergeSkuCountLines(serialLines, bulkUnserializedHoverHintLines(batch.unserializedPackOutOpenBySku));
  }
  return serialLines;
}

function formatManufacturingUnits(n: number): string {
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 1000) / 1000;
  if (Number.isInteger(rounded)) return String(rounded);
  const s = rounded.toFixed(3);
  const trimmed = s.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return trimmed === "" ? "0" : trimmed;
}

function StationSkuHoverHintBody({ lines }: { lines: { sku: string; count: number }[] }): ReactNode {
  return (
    <EntityPeekHint
      kind="sku_quantities"
      lines={lines.map((line) => ({ sku: line.sku, quantity: line.count }))}
    />
  );
}

function batchShippedProgressHoverHint(batch: ProductionCalendarBatch): ReactNode {
  const denom = displayPlannedDenom(batch);
  const hasPlans = batch.batchLinePlans.length > 0;
  const rows: Array<{ label: string; value: ReactNode }> = [];

  if (hasPlans) {
    for (const plan of batch.batchLinePlans) {
      rows.push({
        label: plan.sku,
        value: (
          <span className="tabular-nums">
            {formatManufacturingUnits(shippedCountForSku(batch, plan.sku))}/{plan.plannedQuantity}
          </span>
        ),
      });
    }
  } else if (denom > 0) {
    rows.push({
      label: "Shipped",
      value: (
        <span className="tabular-nums">
          {formatManufacturingUnits(batch.completedCount)} / {denom}
        </span>
      ),
    });
  } else {
    return <span className="text-slate-500">Planned totals use every batch line SKU with a quantity.</span>;
  }

  for (const row of batch.shippedDetailBySkuStation) {
    rows.push({
      label: row.stationName,
      value: <span className="tabular-nums">{row.count} shipped</span>,
    });
  }

  return <HoverHintDefinitionGrid rows={rows} />;
}

function packOutCountFromBatch(batch: ProductionCalendarBatch): number {
  const merged = stationCountsWithOpenBulk(batch);
  return merged[String(SGS_PACK_OUT_STATION_ID)] ?? 0;
}

function shippedAndPackOutProgressPercent(batch: ProductionCalendarBatch, packOutCount: number): number {
  const denom = displayPlannedDenom(batch);
  if (denom <= 0) return 0;
  return Math.min(100, Math.round(((batch.completedCount + packOutCount) / denom) * 100));
}

function stackedShippedAndPackOutBarPercentages(
  batch: ProductionCalendarBatch,
  packOutCount: number,
): { shippedPct: number; packOutPct: number } {
  const denom = displayPlannedDenom(batch);
  if (denom <= 0) return { shippedPct: 0, packOutPct: 0 };
  const rawShipped = (batch.completedCount / denom) * 100;
  const rawPackOut = (packOutCount / denom) * 100;
  const sum = rawShipped + rawPackOut;
  const scale = sum > 100 ? 100 / sum : 1;
  return { shippedPct: rawShipped * scale, packOutPct: rawPackOut * scale };
}

function formatLongDate(value: string): string {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function formatShortDate(value: string): string {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDayName(value: string): string {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { weekday: "short" });
}

function isoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDays(value: string, days: number): string {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  parsed.setDate(parsed.getDate() + days);
  return isoDate(parsed);
}

function eachDay(startInclusive: string, endInclusive: string): string[] {
  const out: string[] = [];
  let cursor = startInclusive;
  // Hard cap to avoid runaway loops if bounds are bad.
  for (let i = 0; i < 366; i++) {
    out.push(cursor);
    if (cursor === endInclusive) break;
    cursor = addDays(cursor, 1);
  }
  return out;
}

function chunkIntoWeeks(days: string[]): string[][] {
  const weeks: string[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

function plannedSerializedDevicesForBatch(batch: ProductionCalendarBatch): number {
  if (batch.batchLinePlans.length > 0) {
    let sum = 0;
    for (const plan of batch.batchLinePlans) {
      if (plan.serialize === true) sum += plan.plannedQuantity;
    }
    return sum;
  }
  return batch.plannedSerializedDevices ?? 0;
}

function plannedUnserializedProductsForBatch(batch: ProductionCalendarBatch): number {
  if (batch.batchLinePlans.length > 0) {
    let sum = 0;
    for (const plan of batch.batchLinePlans) {
      if (plan.serialize === false) sum += plan.plannedQuantity;
    }
    return sum;
  }
  const lineTotal = batch.plannedBatchLineDevices ?? 0;
  if (lineTotal <= 0) return 0;
  return Math.max(0, lineTotal - (batch.plannedSerializedDevices ?? 0));
}

function plannedSerializedDevicesForBatches(batches: ProductionCalendarBatch[]): number {
  let sum = 0;
  for (const batch of batches) sum += plannedSerializedDevicesForBatch(batch);
  return sum;
}

function plannedUnserializedProductsForBatches(batches: ProductionCalendarBatch[]): number {
  let sum = 0;
  for (const batch of batches) sum += plannedUnserializedProductsForBatch(batch);
  return sum;
}

function batchAndDeviceSummary(
  batchCount: number,
  serializedDeviceCount: number,
  unserializedProductCount: number,
): string {
  const parts = [`${batchCount} batch${batchCount === 1 ? "" : "es"}`];
  if (serializedDeviceCount > 0) {
    parts.push(`${serializedDeviceCount.toLocaleString()} serialized`);
  }
  if (unserializedProductCount > 0) {
    parts.push(`${unserializedProductCount.toLocaleString()} unserialized`);
  }
  return parts.join(", ");
}

function batchShipments(batch: ProductionCalendarBatch): ProductionCalendarShipmentSummary[] {
  return batch.shipments ?? [];
}

function shipmentIsStillOpen(summary: ProductionCalendarShipmentSummary): boolean {
  return summary.dateShipped == null || summary.dateShipped === "";
}

function batchHasOutboundShippingBlock(shipments: ProductionCalendarShipmentSummary[]): boolean {
  return shipments.some(
    (shipment) => shipmentIsStillOpen(shipment) && (shipment.outboundBlockedUnits?.length ?? 0) > 0,
  );
}

function outboundBlockedShipments(
  shipments: ProductionCalendarShipmentSummary[],
): ProductionCalendarShipmentSummary[] {
  return shipments.filter(
    (shipment) => shipmentIsStillOpen(shipment) && (shipment.outboundBlockedUnits?.length ?? 0) > 0,
  );
}

function OutboundBlockedIncidentLink({ incidentId }: { incidentId: number | null }): JSX.Element {
  const { openQcIncident } = useQcIncidentModal();
  if (incidentId == null) {
    return <span className="font-medium text-rose-800">Open QC incident</span>;
  }
  return (
    <button
      type="button"
      className="font-medium text-rose-800 underline underline-offset-2 hover:text-rose-900"
      onClick={() => openQcIncident(incidentId, "view")}
    >
      Open QC incident #{incidentId}
    </button>
  );
}

function outboundBlockHoverHint(shipments: ProductionCalendarShipmentSummary[]): ReactNode {
  const blocked = outboundBlockedShipments(shipments);
  return (
    <div className="max-w-[22rem] space-y-2 whitespace-normal text-left text-sm leading-snug">
      <div className="border-b border-rose-100 pb-1 font-medium text-rose-900">Outbound blocked</div>
      <p className="text-xs text-slate-600">
        These open shipments cannot appear on Shipping Outbound until scrapped units are removed and open quality
        incidents are resolved (set resolution date).
      </p>
      {blocked.map((shipment) => (
        <div key={shipment.shipmentId} className="border-b border-slate-50 pb-2 last:border-b-0 last:pb-0">
          <div className="font-semibold text-slate-900">Shipment {shipment.shipmentId}</div>
          <div className="mt-1 space-y-1">
            {(shipment.outboundBlockedUnits ?? []).map((unit) => (
              <div key={unit.serialFciId} className="text-xs text-slate-700">
                {unit.scrap ? (
                  <span className="font-medium text-rose-800">Scrapped serial</span>
                ) : (
                  <OutboundBlockedIncidentLink incidentId={unit.qualityIncidentId} />
                )}
                <div className="tabular-nums text-slate-600">
                  {unit.finishedSku.trim() !== "" ? unit.finishedSku : "Unknown SKU"}
                  {unit.clampSerial ? ` · clamp ${unit.clampSerial}` : null}
                  {unit.pcbaSerial ? ` · PCBA ${unit.pcbaSerial}` : null}
                </div>
                {unit.qualityIncidentIssue ? (
                  <div className="text-slate-500">{unit.qualityIncidentIssue}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BatchOnHoldGlyph(): JSX.Element {
  return (
    <HoverHintTrigger
      align="start"
      trigger={
        <span
          className={cn(
            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-amber-300 bg-amber-50 text-amber-900 outline-none",
            "cursor-help hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-brand-500",
          )}
          tabIndex={0}
          aria-label="Shipment hold. Manufacturing continues; pre-pack and outbound hide this batch until released."
        >
          <PauseCircle className="h-3.5 w-3.5" aria-hidden />
        </span>
      }
    >
      <div className="max-w-[18rem] whitespace-normal text-left text-sm leading-snug">
        <div className="font-semibold text-amber-900">Shipment hold</div>
        <p className="mt-1 text-slate-700">
          Manufacturing continues on the production calendar. Pre-pack and outbound shipping stay hidden until you
          release shipment hold (right-click the batch card or use Batch details).
        </p>
      </div>
    </HoverHintTrigger>
  );
}

function BatchOutboundBlockGlyph({ shipments }: { shipments: ProductionCalendarShipmentSummary[] }) {
  const blockedCount = outboundBlockedShipments(shipments).reduce(
    (sum, shipment) => sum + (shipment.outboundBlockedUnits?.length ?? 0),
    0,
  );
  return (
    <HoverHintTrigger
      align="start"
      trigger={
        <button
          type="button"
          className={cn(
            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-rose-200 bg-rose-50 text-rose-700 outline-none",
            "cursor-help hover:bg-rose-100 focus-visible:ring-2 focus-visible:ring-brand-500",
          )}
          aria-label={`${blockedCount} serialized unit${blockedCount === 1 ? "" : "s"} on this batch block the outbound shipping queue. Hover for details.`}
        >
          <CircleAlert className="h-3.5 w-3.5" aria-hidden />
        </button>
      }
    >
      {outboundBlockHoverHint(shipments)}
    </HoverHintTrigger>
  );
}

function alternateBoardHoverHint(availability: AlternateBoardAvailability): ReactNode {
  const count = availability.alternateCount;
  const headline =
    count === 1 ? "1 eligible alternate" : `${count.toLocaleString()} eligible alternates`;

  return (
    <div className="max-w-[18rem] space-y-2 whitespace-normal text-left">
      <div className="text-xs font-semibold tabular-nums text-slate-900">{headline}</div>
      <HoverHintDefinitionGrid
        rows={availability.byStation.map((row) => ({
          label: row.stationName,
          value: <span className="tabular-nums">{row.count.toLocaleString()}</span>,
        }))}
      />
    </div>
  );
}

function BatchAlternatesGlyph({
  batchId,
  availability,
}: {
  batchId: number;
  availability: AlternateBoardAvailability;
}) {
  return (
    <HoverHintTrigger
      align="start"
      trigger={
        <button
          type="button"
          className={cn(
            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-violet-200 bg-violet-50 text-violet-800 outline-none",
            "cursor-help hover:bg-violet-100 focus-visible:ring-2 focus-visible:ring-brand-500",
          )}
          aria-label={`Alternates board lists ${availability.alternateCount} units eligible for batch ${batchId}. Hover for counts by station.`}
        >
          <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden />
        </button>
      }
    >
      {alternateBoardHoverHint(availability)}
    </HoverHintTrigger>
  );
}

export interface BatchCardProps {
  batch: ProductionCalendarBatch;
  stationsById: Map<number, ProductionCalendarStation>;
  hubWritesAllowed: boolean;
  alternateAvailabilityByBatchId: Map<number, AlternateBoardAvailability>;
  /** When true, hide batch context menu (embedded on Batch details). */
  embedded?: boolean;
}

type ShipmentGlyphMenu =
  | null
  | {
      clientX: number;
      clientY: number;
    };

function shipmentCompositionHoverHint(shipments: ProductionCalendarShipmentSummary[]): ReactNode {
  return (
    <div className="max-w-[22rem] space-y-2 whitespace-normal text-left text-sm leading-snug">
      <div className="border-b border-slate-100 pb-1 font-medium text-slate-800">Shipment compositions</div>
      {shipments.map((shipment) => (
        <div key={shipment.shipmentId} className="border-b border-slate-50 pb-2 last:border-b-0 last:pb-0">
          <div className="font-semibold text-slate-900">
            Shipment {shipment.shipmentId}
            {shipment.sentToPackOut ? (
              <span className="ml-1 block font-normal text-amber-800">
                · Sent to pack out
                {(() => {
                  const summary = formatSentToPackOutAttributionSummary(shipment);
                  return summary ? (
                    <span className="mt-0.5 block text-xs font-normal text-amber-900/90">{summary}</span>
                  ) : null;
                })()}
              </span>
            ) : null}
            {!shipmentIsStillOpen(shipment) ? (
              <span className="ml-1 font-normal text-emerald-800">· Shipped</span>
            ) : null}
          </div>
          {shipment.composition.length === 0 ? (
            <p className="mt-0.5 text-xs text-slate-500">No line-level composition.</p>
          ) : (
            <div className="mt-1 space-y-0.5">
              {shipment.composition.map((line) => (
                <div key={`${shipment.shipmentId}-${line.sku}`} className="tabular-nums text-xs text-slate-700">
                  <span className="font-semibold text-slate-900">{formatManufacturingUnits(line.quantity)}</span>
                  <span> {line.sku}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function unallocatedNonSerializeBatchLines(
  batch: ProductionCalendarBatch,
): { sku: string; plannedQuantity: number }[] {
  const onShipment = new Map(
    (batch.unserializedOnShipmentBySku ?? []).map((row) => [row.sku, row.quantity]),
  );
  return batch.batchLinePlans
    .filter((plan) => plan.serialize === false && (onShipment.get(plan.sku) ?? 0) === 0)
    .map((plan) => ({ sku: plan.sku, plannedQuantity: plan.plannedQuantity }));
}

function batchHasActiveOpenShipment(shipments: ProductionCalendarShipmentSummary[]): boolean {
  return shipments.some((row) => shipmentIsStillOpen(row) && !row.sentToPackOut);
}

function useAllocateUnserializedToShipment(batchId: number, onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const [allocateError, setAllocateError] = useState<string | null>(null);

  const allocateMutation = useMutation({
    mutationFn: async () =>
      apiFetch<{
        ok: true;
        batchId: number;
        shipmentId: number;
        createdShipment: boolean;
        linesAllocated: { batchItemId: number; sku: string; quantity: number }[];
      }>("/api/manufacturing/production-calendar/allocate-unserialized-to-active-shipment", {
        method: "POST",
        body: { batchId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "production-calendar"], exact: false });
      setAllocateError(null);
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError ? error.message : "Could not allocate unserialized lines.";
      setAllocateError(message);
    },
  });

  return { allocateMutation, allocateError, setAllocateError };
}

function AllocateUnserializedMenuItem({
  batch,
  shipments,
  hubWritesAllowed,
  disabled = false,
  onClick,
  className,
}: {
  batch: ProductionCalendarBatch;
  shipments: ProductionCalendarShipmentSummary[];
  hubWritesAllowed: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}) {
  const unallocatedBulkLines = useMemo(() => unallocatedNonSerializeBatchLines(batch), [batch]);
  const canAllocateBulk = unallocatedBulkLines.length > 0;
  const activeOpenShipmentExists = useMemo(() => batchHasActiveOpenShipment(shipments), [shipments]);

  if (!canAllocateBulk) {
    return null;
  }

  const menuItemClassName = cn(
    "block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60",
    className,
  );

  if (!hubWritesAllowed) {
    return (
      <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="block">
        <button type="button" role="menuitem" disabled className={cn(menuItemClassName, "cursor-not-allowed text-slate-500 opacity-60")}>
          Allocate unserialized to shipment
        </button>
      </ReadOnlyHoverLayer>
    );
  }

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className={menuItemClassName}
      onClick={onClick}
    >
      <span className="block leading-snug">
        <span className="block">Allocate unserialized to shipment</span>
        <span className="mt-0.5 block text-xs italic text-slate-600">
          {unallocatedBulkLines.map((line) => line.sku).join(", ")}
          {!activeOpenShipmentExists ? " · will open a new shipment" : ""}
        </span>
      </span>
    </button>
  );
}

function BatchShipmentGlyph({
  batch,
  shipments,
  hubWritesAllowed,
}: {
  batch: ProductionCalendarBatch;
  shipments: ProductionCalendarShipmentSummary[];
  hubWritesAllowed: boolean;
}) {
  const queryClient = useQueryClient();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menu, setMenu] = useState<ShipmentGlyphMenu>(null);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const [lockError, setLockError] = useState<string | null>(null);
  const { allocateMutation, allocateError, setAllocateError } = useAllocateUnserializedToShipment(
    batch.batchId,
    () => {
      setMenu(null);
      setLockError(null);
    },
  );

  const unallocatedBulkLines = useMemo(() => unallocatedNonSerializeBatchLines(batch), [batch]);
  const canAllocateBulk = unallocatedBulkLines.length > 0;

  const lockableShipments = useMemo(
    () => shipments.filter((row) => shipmentIsStillOpen(row) && !row.sentToPackOut),
    [shipments],
  );

  const lockMutation = useMutation({
    mutationFn: async (shipmentId: number) =>
      apiFetch<{ ok: true; shipmentId: number }>(
        "/api/manufacturing/production-calendar/lock-partial-shipment",
        { method: "POST", body: { shipmentId } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "production-calendar"], exact: false });
      setMenu(null);
      setLockError(null);
    },
    onError: (error: unknown) => {
      const message = error instanceof ApiError ? error.message : "Could not lock shipment.";
      setLockError(message);
    },
  });

  const repositionMenu = useCallback(() => {
    if (!menu) {
      setMenuBox(null);
      return;
    }
    setMenuBox(
      computePointAnchoredContextMenu({
        clientX: menu.clientX,
        clientY: menu.clientY,
        menuWidth: 14 * 16,
        maxHeightCap: Math.min(360, Math.floor(window.innerHeight * 0.85)),
      }),
    );
  }, [menu]);

  useLayoutEffect(() => {
    if (!menu) {
      setMenuBox(null);
      return;
    }
    repositionMenu();
    const onLayout = (): void => {
      repositionMenu();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    window.visualViewport?.addEventListener("resize", onLayout);
    window.visualViewport?.addEventListener("scroll", onLayout);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
      window.visualViewport?.removeEventListener("resize", onLayout);
      window.visualViewport?.removeEventListener("scroll", onLayout);
    };
  }, [menu, repositionMenu]);

  useEffect(() => {
    if (!menu) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (menuRef.current?.contains(event.target as Node)) return;
      setMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setMenu(null);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menu]);

  const openMenu = (event: ReactUiMouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setLockError(null);
    setAllocateError(null);
    setMenu({ clientX: event.clientX, clientY: event.clientY });
  };

  const lockPending = lockMutation.isPending;
  const allocatePending = allocateMutation.isPending;
  const actionPending = lockPending || allocatePending;

  return (
    <>
      <HoverHintTrigger
        align="start"
        trigger={
          <button
            type="button"
            className={cn(
              "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-sky-200 bg-sky-50 text-sky-800 outline-none",
              "cursor-context-menu hover:bg-sky-100 focus-visible:ring-2 focus-visible:ring-brand-500",
            )}
            aria-label={`Batch has ${shipments.length} shipment${shipments.length === 1 ? "" : "s"}. Hover for composition. Right-click for actions.`}
            onContextMenu={openMenu}
          >
            <Package className="h-3.5 w-3.5" aria-hidden />
          </button>
        }
      >
        {shipmentCompositionHoverHint(shipments)}
      </HoverHintTrigger>

      {lockError ? <p className="mt-0.5 max-w-[14rem] text-[10px] leading-tight text-rose-700">{lockError}</p> : null}
      {allocateError ? (
        <p className="mt-0.5 max-w-[14rem] text-[10px] leading-tight text-rose-700">{allocateError}</p>
      ) : null}

      {menu && menuBox
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: menuBox.left,
                top: menuBox.top,
                width: menuBox.width,
                maxHeight: menuBox.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              aria-label="Shipment actions"
            >
              {!hubWritesAllowed ? (
                <>
                  <AllocateUnserializedMenuItem
                    batch={batch}
                    shipments={shipments}
                    hubWritesAllowed={false}
                    onClick={() => {}}
                  />
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="block">
                    <button
                      type="button"
                      role="menuitem"
                      disabled
                      className="block w-full cursor-not-allowed px-3 py-2 text-left text-sm text-slate-500 opacity-60"
                    >
                      Lock partial shipment
                    </button>
                  </ReadOnlyHoverLayer>
                </>
              ) : (
                <>
                  <AllocateUnserializedMenuItem
                    batch={batch}
                    shipments={shipments}
                    hubWritesAllowed={hubWritesAllowed}
                    disabled={actionPending}
                    onClick={() => {
                      setAllocateError(null);
                      allocateMutation.mutate();
                    }}
                  />
                  {!canAllocateBulk ? (
                    <div className="px-3 py-2 text-xs text-slate-500">
                      No non-serialized batch lines are waiting for shipment allocation.
                    </div>
                  ) : null}
                  {lockableShipments.length === 0 ? (
                    <div className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
                      Nothing to lock. Shipments are shipped or already sent to pack out.
                    </div>
                  ) : (
                    lockableShipments.map((row) => (
                      <button
                        key={row.shipmentId}
                        type="button"
                        role="menuitem"
                        disabled={actionPending}
                        className="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
                        onClick={() => {
                          setLockError(null);
                          lockMutation.mutate(row.shipmentId);
                        }}
                      >
                        {lockableShipments.length > 1 ? (
                          <span className="block leading-snug">
                            <span className="block">Lock partial shipment</span>
                            <span className="mt-0.5 block text-xs italic text-slate-600">
                              Shipment {row.shipmentId}
                            </span>
                          </span>
                        ) : (
                          "Lock partial shipment"
                        )}
                      </button>
                    ))
                  )}
                </>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export function BatchCard({
  batch,
  stationsById,
  hubWritesAllowed,
  alternateAvailabilityByBatchId,
  embedded = false,
}: BatchCardProps) {
  const navigate = useNavigate();
  const shipments = batchShipments(batch);
  const alternateAvailability = alternateAvailabilityByBatchId.get(batch.batchId) ?? null;

  const cardMenuRef = useRef<HTMLDivElement | null>(null);
  const [cardMenu, setCardMenu] = useState<ShipmentGlyphMenu>(null);
  const [cardMenuBox, setCardMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const [holdCallout, setHoldCallout] = useState<{ shipmentOnHold: boolean } | null>(null);
  const [alternateOrderModalOpen, setAlternateOrderModalOpen] = useState(false);
  const { allocateMutation, allocateError, setAllocateError } = useAllocateUnserializedToShipment(
    batch.batchId,
    () => setCardMenu(null),
  );
  const unallocatedBulkLines = useMemo(() => unallocatedNonSerializeBatchLines(batch), [batch]);
  const canAllocateBulk = unallocatedBulkLines.length > 0;
  const allocatePending = allocateMutation.isPending;

  const repositionCardMenu = useCallback(() => {
    if (!cardMenu) {
      setCardMenuBox(null);
      return;
    }
    setCardMenuBox(
      computePointAnchoredContextMenu({
        clientX: cardMenu.clientX,
        clientY: cardMenu.clientY,
        menuWidth: 14 * 16,
        maxHeightCap: Math.min(320, Math.floor(window.innerHeight * 0.85)),
      }),
    );
  }, [cardMenu]);

  useLayoutEffect(() => {
    if (!cardMenu) {
      setCardMenuBox(null);
      return;
    }
    repositionCardMenu();
    const onLayout = (): void => {
      repositionCardMenu();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    window.visualViewport?.addEventListener("resize", onLayout);
    window.visualViewport?.addEventListener("scroll", onLayout);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
      window.visualViewport?.removeEventListener("resize", onLayout);
      window.visualViewport?.removeEventListener("scroll", onLayout);
    };
  }, [cardMenu, repositionCardMenu]);

  useEffect(() => {
    if (!cardMenu) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (cardMenuRef.current?.contains(event.target as Node)) return;
      setCardMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setCardMenu(null);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [cardMenu]);

  const denom = displayPlannedDenom(batch);
  const packOutCount = packOutCountFromBatch(batch);
  const bulkQcOpenQty = batch.unserializedQcOpenQty ?? 0;
  const bulkPackOutOpenQty = batch.unserializedPackOutOpenQty ?? 0;
  const progressAccounted =
    Math.max(batch.trackedTotal, batch.completedCount) + bulkQcOpenQty + bulkPackOutOpenQty;
  const notStartedCount = batch.unassignedCount + Math.max(0, denom - progressAccounted);
  const notStartedDeviceLines = notStartedHoverHintLinesBySku(batch);
  const notStartedClampLines = notStartedLinesClampUnitsFromDevices(batch, notStartedDeviceLines);
  const showNotStartedRow = notStartedCount > 0 || notStartedDeviceLines.length > 0;
  const notStartedRowTotal =
    notStartedDeviceLines.length > 0
      ? notStartedClampLines.reduce((sum, row) => sum + row.count, 0)
      : notStartedCount;

  const stationLines = Object.entries(stationCountsWithOpenBulk(batch))
    .map(([stationIdRaw, count]) => {
      if (stationIdRaw === "null") return null;
      const stationId = Number(stationIdRaw);
      if (!Number.isFinite(stationId)) return null;
      const station = stationsById.get(stationId);
      return {
        stationId,
        name: station?.name ?? `Station ${stationId}`,
        isTerminal: station?.isTerminal ?? false,
        count,
      };
    })
    .filter((line): line is NonNullable<typeof line> => line != null && line.count > 0)
    .sort((left, right) =>
      compareSgsProductionCalendarStationOrder(
        { stationId: left.stationId, isTerminal: left.isTerminal, deviceCount: left.count },
        { stationId: right.stationId, isTerminal: right.isTerminal, deviceCount: right.count },
      ),
    );

  const combinedPercent = shippedAndPackOutProgressPercent(batch, packOutCount);
  const { shippedPct, packOutPct } = stackedShippedAndPackOutBarPercentages(batch, packOutCount);

  const subtitleSkuList = subtitleSkuOrder(batch);

  const editBatchPath = buildManufacturingEditBatchPathForBatch(batch.batchId);
  const salesOrderContextSummary = batchSalesOrderContextAriaSummary(batch);
  const headline = batchHeadline(batch);
  const batchShipmentOnHold = batch.shipmentOnHold === true;

  return (
    <>
      <div
        className={cn(
          "min-w-0 overflow-hidden rounded-md border bg-white p-2 shadow-sm",
          batchShipmentOnHold ? "border-amber-300 bg-amber-50/40" : "border-slate-200",
        )}
        data-production-calendar-batch={productionCalendarBatchAnchorKey(batch.batchId)}
        onContextMenu={
          embedded
            ? undefined
            : (event) => {
                event.preventDefault();
                setCardMenu({ clientX: event.clientX, clientY: event.clientY });
              }
        }
      >
      <div className="min-w-0">
        <div className="flex flex-wrap items-start gap-1.5">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {batchHasSalesOrderContext(batch) ? (
              <HoverHintContent align="start" hoverHint={batchSalesOrderContextHoverHint(batch)}>
                <span
                  className="font-mono text-sm font-bold text-slate-900"
                  aria-label={`${headline}, ${salesOrderContextSummary}`}
                >
                  {headline}
                </span>
              </HoverHintContent>
            ) : (
              <span className="font-mono text-sm font-bold text-slate-900">{headline}</span>
            )}
          </div>
          <div className="flex shrink-0 flex-row items-start gap-1">
            {batchShipmentOnHold ? <BatchOnHoldGlyph /> : null}
            <BatchTeamNotesGlyph
              batchId={batch.batchId}
              teamNoteCount={batch.teamNoteCount ?? 0}
              latestTeamNote={batch.latestTeamNote ?? null}
              hubWritesAllowed={hubWritesAllowed}
            />
            {batchHasOutboundShippingBlock(shipments) ? (
              <BatchOutboundBlockGlyph shipments={shipments} />
            ) : null}
            {alternateAvailability != null ? (
              <BatchAlternatesGlyph batchId={batch.batchId} availability={alternateAvailability} />
            ) : null}
            {shipments.length > 0 ? (
              <BatchShipmentGlyph batch={batch} shipments={shipments} hubWritesAllowed={hubWritesAllowed} />
            ) : null}
          </div>
        </div>
        <HoverHintTrigger
          align="start"
          block
          trigger={
            <p
              className="block w-full min-w-0 cursor-help truncate rounded px-0.5 text-[11px] text-slate-600 outline-none -mx-0.5 focus-visible:ring-2 focus-visible:ring-brand-400"
              tabIndex={0}
              aria-label={batchSubtitleAriaLabel(batch)}
            >
              {formatBatchSubtitleLines(subtitleSkuList)}
            </p>
          }
        >
          {batchLineQuantitiesHoverHint(batch)}
        </HoverHintTrigger>
      </div>

      <div className="group/pbatch relative mt-2">
        <HoverHintTrigger
          align="center"
          trigger={
            <div className="w-full cursor-help outline-none" tabIndex={0} role="presentation">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="text-slate-500">
                  <span className="font-semibold tabular-nums text-slate-900">{formatManufacturingUnits(batch.completedCount)}</span>
                  {denom > 0 ? (
                    <>
                      <span className="text-slate-400"> / </span>
                      <span className="tabular-nums text-slate-700">{denom}</span>
                    </>
                  ) : null}
                  <span className="ml-1">shipped</span>
                </span>
                <span className="font-semibold tabular-nums text-slate-700">{combinedPercent}%</span>
              </div>
              <div className="mt-1 flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-emerald-500" style={{ width: `${shippedPct}%` }} aria-hidden />
                <div className="h-full bg-amber-400" style={{ width: `${packOutPct}%` }} aria-hidden />
              </div>
            </div>
          }
        >
          {batchShippedProgressHoverHint(batch)}
        </HoverHintTrigger>
      </div>

      {stationLines.length > 0 || showNotStartedRow ? (
        <div className="mt-2 space-y-0.5 border-t border-slate-100 pt-2">
          {showNotStartedRow ? (
            <div className="group/notstarted relative rounded px-0.5 -mx-0.5 outline-none focus-within:ring-2 focus-within:ring-brand-400">
              <HoverHintTrigger
                align="start"
                trigger={
                  <div className="flex w-full cursor-help items-center justify-between gap-2 text-[11px] outline-none">
                    <span className="min-w-0 truncate text-slate-600">Not started</span>
                    <span className="font-semibold tabular-nums text-slate-900">
                      {formatManufacturingUnits(notStartedRowTotal)}
                    </span>
                  </div>
                }
              >
                <div className="mb-1 font-medium text-slate-800">Planned minus progress</div>
                {notStartedClampLines.length > 0 ? (
                  <StationSkuHoverHintBody lines={notStartedClampLines} />
                ) : (
                  <span className="text-slate-500">
                    {formatManufacturingUnits(notStartedCount)} manufacturing units not attributed to specific batch
                    lines in this view (often unassigned serials).
                  </span>
                )}
              </HoverHintTrigger>
            </div>
          ) : null}
          {stationLines.map((line) => (
            <div
              key={line.stationId}
              className="group/station relative -mx-0.5 flex rounded px-0.5 text-[11px] outline-none focus-within:ring-2 focus-within:ring-brand-400"
            >
              <HoverHintTrigger
                align="start"
                trigger={
                  <div
                    className="flex w-full cursor-help items-center justify-between gap-2 text-[11px] outline-none"
                    tabIndex={0}
                  >
                    <span
                      className={cn(
                        "min-w-0 truncate",
                        line.isTerminal ? "font-medium text-emerald-700" : "text-slate-600",
                      )}
                    >
                      {line.name}
                    </span>
                    <span className="font-semibold tabular-nums text-slate-900">
                      {formatManufacturingUnits(line.count)}
                    </span>
                  </div>
                }
              >
                <div className="mb-1 font-medium text-slate-800">{line.name}</div>
                <StationSkuHoverHintBody lines={stationRowHoverHintLines(batch, line.stationId)} />
              </HoverHintTrigger>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 border-t border-slate-100 pt-2 text-[11px] italic text-slate-500">
          No serials tracked yet.
        </p>
      )}
      </div>
      {!embedded && cardMenu && cardMenuBox
        ? createPortal(
            <div
              ref={cardMenuRef}
              className="fixed overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: cardMenuBox.left,
                top: cardMenuBox.top,
                width: cardMenuBox.width,
                maxHeight: cardMenuBox.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              aria-label="Batch actions"
            >
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => {
                  setCardMenu(null);
                  void navigate(buildManufacturingBatchDetailsPath(batch.batchId));
                }}
              >
                Batch details
              </button>
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => {
                  setCardMenu(null);
                  void navigate(editBatchPath);
                }}
              >
                Edit batch
              </button>
              <button
                type="button"
                role="menuitem"
                className="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => {
                  setCardMenu(null);
                  void navigate(
                    `/manufacturing-management/alternates?search=${encodeURIComponent(`batch:${String(batch.batchId)}`)}`,
                  );
                }}
              >
                Find alternates
              </button>
              {canAllocateBulk ? (
                <AllocateUnserializedMenuItem
                  batch={batch}
                  shipments={shipments}
                  hubWritesAllowed={hubWritesAllowed}
                  disabled={allocatePending}
                  className="border-t border-slate-100"
                  onClick={() => {
                    setAllocateError(null);
                    allocateMutation.mutate();
                  }}
                />
              ) : null}
              {hubWritesAllowed ? (
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                  onClick={() => {
                    setCardMenu(null);
                    setAlternateOrderModalOpen(true);
                  }}
                >
                  Alternate order
                </button>
              ) : (
                <ReadOnlyHoverLayer active hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="block">
                  <button
                    type="button"
                    role="menuitem"
                    disabled
                    className="block w-full cursor-not-allowed px-3 py-2 text-left text-sm text-slate-500 opacity-60"
                  >
                    Alternate order
                  </button>
                </ReadOnlyHoverLayer>
              )}
              {hubWritesAllowed ? (
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                  onClick={() => {
                    setCardMenu(null);
                    setHoldCallout({ shipmentOnHold: !batchShipmentOnHold });
                  }}
                >
                  {batchShipmentOnHold ? "Release shipment hold" : "Place shipment hold"}
                </button>
              ) : (
                <ReadOnlyHoverLayer active hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="block">
                  <button
                    type="button"
                    role="menuitem"
                    disabled
                    className="block w-full cursor-not-allowed border-t border-slate-100 px-3 py-2 text-left text-sm text-slate-500 opacity-60"
                  >
                    {batchShipmentOnHold ? "Release shipment hold" : "Place shipment hold"}
                  </button>
                </ReadOnlyHoverLayer>
              )}
            </div>,
            document.body,
          )
        : null}
      <CreateAlternateOrderModal
        open={alternateOrderModalOpen}
        onClose={() => setAlternateOrderModalOpen(false)}
        source="production_calendar"
        prefill={{
          batchId: batch.batchId,
          targetDate: batch.mfrShipByDate?.trim().slice(0, 10) ?? null,
          skuOptions: batchAlternateOrderSkuOptions(batch),
        }}
      />
      {holdCallout != null ? (
        <BatchOnHoldReasonCallout
          batchId={batch.batchId}
          shipmentOnHold={holdCallout.shipmentOnHold}
          hubWritesAllowed={hubWritesAllowed}
          onClose={() => setHoldCallout(null)}
        />
      ) : null}
      {allocateError ? (
        <p className="mt-1 text-[10px] leading-tight text-rose-700">{allocateError}</p>
      ) : null}
    </>
  );
}

interface DayCellProps {
  date: string;
  batches: ProductionCalendarBatch[];
  stationsById: Map<number, ProductionCalendarStation>;
  hubWritesAllowed: boolean;
  alternateAvailabilityByBatchId: Map<number, AlternateBoardAvailability>;
  isToday: boolean;
  isWeekend: boolean;
}

function DayCell({
  date,
  batches,
  stationsById,
  hubWritesAllowed,
  alternateAvailabilityByBatchId,
  isToday,
  isWeekend,
}: DayCellProps) {
  const serializedPlannedTotal = plannedSerializedDevicesForBatches(batches);
  const unserializedPlannedTotal = plannedUnserializedProductsForBatches(batches);
  return (
    <div
      className={cn(
        "flex min-h-[120px] min-w-0 flex-col gap-2 rounded-md border bg-white p-2",
        isToday ? "border-brand-400 ring-1 ring-brand-200" : "border-slate-200",
        isWeekend ? "bg-slate-50/60" : "",
      )}
    >
      <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {formatDayName(date)}
        </span>
        <div className="flex flex-col items-end gap-0.5 sm:text-right">
          <span
            className={cn(
              "text-xs font-semibold tabular-nums",
              isToday ? "text-brand-700" : "text-slate-700",
            )}
          >
            {formatShortDate(date)}
          </span>
          {batches.length > 0 ? (
            <span className="text-[10px] font-medium tabular-nums text-slate-500">
              {batchAndDeviceSummary(batches.length, serializedPlannedTotal, unserializedPlannedTotal)}
            </span>
          ) : null}
        </div>
      </div>
      {batches.length === 0 ? (
        <p className="my-auto text-center text-[11px] italic text-slate-300">No batches</p>
      ) : (
        <div className="min-w-0 space-y-1.5">
          {batches.map((batch) => (
            <BatchCard
              key={batch.batchId}
              batch={batch}
              stationsById={stationsById}
              hubWritesAllowed={hubWritesAllowed}
              alternateAvailabilityByBatchId={alternateAvailabilityByBatchId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function startOfCurrentWeekIso(): string {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday
  const offsetToMonday = (dayOfWeek + 6) % 7;
  now.setDate(now.getDate() - offsetToMonday);
  return isoDate(now);
}

function parseProductionCalendarStartDateParam(raw: string | null): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(trimmed)) return null;
  return trimmed;
}

function setProductionCalendarStartDateParam(
  params: URLSearchParams,
  startDate: string,
): void {
  const defaultStart = startOfCurrentWeekIso();
  if (startDate === defaultStart) {
    params.delete("startDate");
  } else {
    params.set("startDate", startDate);
  }
}

function ProductionCalendarWindowControls({
  startDate,
  endDate,
  isFetching,
  onShiftPeriod,
}: {
  startDate: string | null;
  endDate: string | null;
  isFetching: boolean;
  onShiftPeriod: (direction: -1 | 1) => void;
}) {
  return (
    <div className="flex min-w-[16rem] items-center gap-2">
        <Button
          type="button"
          size="md"
          variant="secondary"
          className="shrink-0 px-2"
          onClick={() => onShiftPeriod(-1)}
          disabled={isFetching}
          aria-label="Previous period"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </Button>
        <span className="min-w-0 flex-1 text-center text-sm font-semibold text-slate-900">
          {startDate != null && endDate != null
            ? `${formatLongDate(startDate)} - ${formatLongDate(endDate)}`
            : "—"}
        </span>
        <Button
          type="button"
          size="md"
          variant="secondary"
          className="shrink-0 px-2"
          onClick={() => onShiftPeriod(1)}
          disabled={isFetching}
          aria-label="Next period"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
    </div>
  );
}

export function ProductionCalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const startDate =
    parseProductionCalendarStartDateParam(searchParams.get("startDate")) ?? startOfCurrentWeekIso();
  const weeks = DEFAULT_WEEKS;
  const includeOverdue = searchParams.get("includeOverdue") !== "0";
  const searchSku = (searchParams.get("sku") ?? "").trim();
  const [skuPickerQuery, setSkuPickerQuery] = useState("");
  const todayIso = isoDate(new Date());
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const periodDayShift = weeks * 7;

  const shiftCalendarPeriod = useCallback(
    (direction: -1 | 1) => {
      const nextStart = addDays(startDate, direction * periodDayShift);
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        setProductionCalendarStartDateParam(next, nextStart);
        return next;
      });
    },
    [periodDayShift, setSearchParams, startDate],
  );

  const calendarQueryKey = [
    "manufacturing",
    "production-calendar",
    startDate,
    weeks,
    includeOverdue,
    searchSku,
  ] as const;

  const calendarQuery = useQuery<ProductionCalendarResponse, ApiError>({
    queryKey: calendarQueryKey,
    queryFn: () => fetchProductionCalendar(startDate, weeks, includeOverdue, searchSku, { background: true }),
    refetchOnWindowFocus: false,
  });

  const skuListQuery = useQuery<{ skus: { sku: string }[]; total_count?: number }, ApiError>({
    queryKey: ["mrp", "skus", "production-calendar", skuPickerQuery],
    queryFn: () =>
      apiFetch<{ skus: { sku: string }[]; total_count?: number }>("/api/mrp/skus", {
        query: { q: skuPickerQuery, page: "1", page_size: "12" },
      }),
    enabled: skuPickerQuery.trim().length >= 2,
    staleTime: 60_000,
  });

  const skuPickerOptions = useMemo(
    () =>
      (skuListQuery.data?.skus ?? []).map((row) => ({
        value: row.sku,
        label: row.sku,
      })),
    [skuListQuery.data?.skus],
  );

  const setSearchSkuParam = useCallback(
    (nextSku: string) => {
      const trimmed = nextSku.trim();
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        if (trimmed === "") {
          next.delete("sku");
        } else {
          next.set("sku", trimmed);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const calendarHubCacheChannel = useMemo(
    () => productionCalendarHubCacheChannel(startDate, weeks, includeOverdue, searchSku),
    [startDate, weeks, includeOverdue, searchSku],
  );

  useHubCacheStream({
    channels: [calendarHubCacheChannel],
    onEvent: (event) => {
      if (event.phase === "patch" && event.production_calendar_batch != null) {
        queryClient.setQueryData<ProductionCalendarResponse>(calendarQueryKey, (previous) => {
          if (previous == null) {
            return previous;
          }
          return mergeProductionCalendarBatchPatch(
            previous,
            event.production_calendar_batch as unknown as ProductionCalendarBatch,
          );
        });
        return;
      }
      if (event.phase === "ready") {
        void queryClient.invalidateQueries({ queryKey: calendarQueryKey });
      }
    },
  });

  const refreshProductionCalendar = useCallback(async (): Promise<void> => {
    await queryClient.fetchQuery({
      queryKey: calendarQueryKey,
      queryFn: () => fetchProductionCalendar(startDate, weeks, includeOverdue, searchSku, { refresh: true }),
    });
  }, [queryClient, calendarQueryKey, startDate, weeks, includeOverdue, searchSku]);

  const alternatesQuery = useQuery<ManufacturingAlternatesBoardApiResponse, ApiError>({
    queryKey: ["manufacturing", "alternates"],
    queryFn: () => apiFetch<ManufacturingAlternatesBoardApiResponse>("/api/manufacturing/alternates"),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const alternateAvailabilityByBatchId = useMemo(() => {
    const groups = alternatesQuery.data?.groups ?? [];
    const map = new Map<number, AlternateBoardAvailability>();
    const batches = [
      ...(calendarQuery.data?.batches ?? []),
      ...(calendarQuery.data?.overdueBatches ?? []),
      ...(calendarQuery.data?.searchResults ?? []),
    ];
    const seen = new Set<number>();
    for (const batch of batches) {
      if (seen.has(batch.batchId)) continue;
      seen.add(batch.batchId);
      const availability = alternateBoardAvailabilityForBatch(groups, batch.batchId);
      if (availability != null) map.set(batch.batchId, availability);
    }
    return map;
  }, [alternatesQuery.data?.groups, calendarQuery.data?.batches, calendarQuery.data?.overdueBatches]);

  const stationsById = useMemo(() => {
    const map = new Map<number, ProductionCalendarStation>();
    for (const station of calendarQuery.data?.stations ?? []) {
      map.set(station.id, station);
    }
    return map;
  }, [calendarQuery.data?.stations]);

  const batchesByDate = useMemo(() => {
    const map = new Map<string, ProductionCalendarBatch[]>();
    for (const batch of calendarQuery.data?.batches ?? []) {
      const key = batch.mfrShipByDate ?? "";
      if (key === "") continue;
      const list = map.get(key) ?? [];
      list.push(batch);
      map.set(key, list);
    }
    for (const [, list] of map) {
      list.sort((left, right) => {
        const leftLoc = left.locationName.localeCompare(right.locationName, undefined, { sensitivity: "base" });
        if (leftLoc !== 0) return leftLoc;
        return left.batchId - right.batchId;
      });
    }
    return map;
  }, [calendarQuery.data?.batches]);

  const weekRows = useMemo(() => {
    if (!calendarQuery.data) return [] as { weekStart: string; days: string[] }[];
    const days = eachDay(calendarQuery.data.startDate, calendarQuery.data.endDate);
    return chunkIntoWeeks(days).map((weekDays) => ({
      weekStart: weekDays[0]!,
      days: weekDays,
    }));
  }, [calendarQuery.data]);

  const overdueBatches = calendarQuery.data?.overdueBatches ?? [];
  const searchResults = calendarQuery.data?.searchResults ?? [];
  const activeSearchSku = calendarQuery.data?.searchSku ?? searchSku;
  const searchResultsTruncated = calendarQuery.data?.searchResultsTruncated === true;

  return (
    <div className="space-y-6">
      <header className="sticky top-0 z-20 -mx-3 border-b border-slate-200 bg-slate-50 px-3 pb-3 sm:-mx-4 sm:px-4 lg:-mx-6 lg:px-6 2xl:-mx-10 2xl:px-10 lg:flex lg:flex-row lg:items-center lg:justify-between lg:gap-3 flex flex-col gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarRange className="h-6 w-6 text-brand-600" aria-hidden />
            <h1 className="text-xl font-semibold text-slate-900">Production calendar</h1>
            <MetricCardHelp
              ariaLabel="How this calendar is built"
              hoverHintAlign="start"
              hint={
                <span className="block text-sm text-slate-600">
                  <span className="mb-2 block border-b border-slate-100 pb-2 text-slate-700">
                    The grid loads {weeks} consecutive calendar weeks from the Monday shown in the window control. Use the
                    previous and next controls there to move by one full window at a time, or Refresh to reload the
                    latest numbers from the server.
                  </span>
                  <span className="font-medium text-slate-800">SGS batches only</span>:{" "}
                  <code className="rounded bg-slate-100 px-1 text-xs">public.batch.location_id</code> matches the MRP{" "}
                  <code className="rounded bg-slate-100 px-1 text-xs">sgs</code> site (Smart Grid Solutions). Rows sit on
                  each batch&apos;s <span className="font-medium text-slate-800">mfr ship-by date</span> from{" "}
                  <code className="rounded bg-slate-100 px-1 text-xs">public.batch</code>. Per-batch counts are the current{" "}
                  <code className="rounded bg-slate-100 px-1 text-xs">public.serial_fci.station_id</code> for every serial
                  in that batch (via <code className="rounded bg-slate-100 px-1 text-xs">public.batch_item</code>), not scan
                  history. &quot;Shipped&quot; progress uses stations named Shipped/Shipping or a serial&apos;s&nbsp;
                  <code className="rounded bg-slate-100 px-1 text-xs">shipment_id</code> whose&nbsp;
                  <code className="rounded bg-slate-100 px-1 text-xs">public.shipment.date_shipped</code> is set — with
                  serialized lines scaled by&nbsp;
                  <code className="rounded bg-slate-100 px-1 text-xs">sku_divisors</code> so totals line up with batch line&nbsp;
                  <code className="rounded bg-slate-100 px-1 text-xs">plannedQuantity</code> units (kits / clamps), same idea
                  as the Shipped station row.
                </span>
              }
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <div className="min-w-[16rem]">
            <SkuSearchableSelect
              value={searchSku}
              onValueChange={(option) => setSearchSkuParam(option?.value ?? "")}
              options={skuPickerOptions}
              searchValue={skuPickerQuery}
              onSearchChange={setSkuPickerQuery}
              placeholder="Filter by SKU"
              searchPlaceholder="Search catalog SKUs"
              emptyMessage={skuPickerQuery.trim().length >= 2 ? "No SKUs found." : "Type at least 2 characters."}
              isLoading={skuListQuery.isFetching}
              selectedLabelOverride={searchSku}
            />
          </div>
          <ProductionCalendarWindowControls
            startDate={calendarQuery.data?.startDate ?? null}
            endDate={calendarQuery.data?.endDate ?? null}
            isFetching={calendarQuery.isFetching}
            onShiftPeriod={shiftCalendarPeriod}
          />
          {searchSku !== "" ? (
            <Button type="button" variant="secondary" onClick={() => setSearchSkuParam("")}>
              Clear SKU search
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            onClick={() => void refreshProductionCalendar()}
            disabled={calendarQuery.isFetching}
          >
            <RefreshCw
              className={cn("mr-2 h-4 w-4", calendarQuery.isFetching ? "animate-spin" : "")}
              aria-hidden
            />
            Refresh
            {calendarQuery.data?.revalidating ? (
              <span className="ml-1 text-xs font-normal text-slate-500">· refreshing…</span>
            ) : null}
          </Button>
        </div>
      </header>

      {activeSearchSku !== "" ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Search results for {activeSearchSku}
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            {calendarQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading batch search…</p>
            ) : searchResults.length === 0 ? (
              <p className="text-sm text-slate-600">No SGS batches found with batch line SKU {activeSearchSku}.</p>
            ) : (
              <>
                <p className="text-xs text-slate-500">
                  {searchResults.length} batch{searchResults.length === 1 ? "" : "es"} with this line SKU
                  {searchResultsTruncated
                    ? ` (showing the ${searchResults.length} most recent by manufacturer ship-by date; refine the SKU if needed).`
                    : ", newest ship-by first. Batches already on the grid are listed here too when they match."}
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7">
                  {searchResults.map((batch) => (
                    <div key={`search-${batch.batchId}`} className="min-w-0 space-y-1">
                      <p className="text-xs font-medium text-slate-500">
                        Ship-by {batch.mfrShipByDate ? formatLongDate(batch.mfrShipByDate) : "—"}
                      </p>
                      <BatchCard
                        batch={batch}
                        stationsById={stationsById}
                        hubWritesAllowed={hubWritesAllowed}
                        alternateAvailabilityByBatchId={alternateAvailabilityByBatchId}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardBody>
        </Card>
      ) : null}

      {overdueBatches.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" aria-hidden />
              Overdue (ship-by passed, serials not yet shipped)
              <HoverHintWithGlyph
                hoverHintAriaLabel="What overdue means"
                hoverHintContent={
                  <>
                    {overdueBatches.length} batch{overdueBatches.length === 1 ? "" : "es"} whose manufacturer ship-by
                    date is before today ({formatLongDate(todayIso)}) and that still have at least one serial that is
                    not shipped: no terminal station and no shipment with date_shipped. Limited to the most recent 200.
                  </>
                }
              />
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7">
              {overdueBatches.map((batch) => (
                <div key={batch.batchId} className="min-w-0 space-y-1">
                  <p className="text-xs font-medium text-slate-500">
                    Was due {batch.mfrShipByDate ? formatLongDate(batch.mfrShipByDate) : "—"}
                  </p>
                  <BatchCard
                    batch={batch}
                    stationsById={stationsById}
                    hubWritesAllowed={hubWritesAllowed}
                    alternateAvailabilityByBatchId={alternateAvailabilityByBatchId}
                  />
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      ) : null}

      <div className="space-y-4">
        {calendarQuery.isLoading ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500">Loading production calendar…</p>
        ) : calendarQuery.error ? (
          <p className="px-5 py-8 text-center text-sm text-rose-700">{calendarQuery.error.message}</p>
        ) : weekRows.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-slate-600">No window data.</p>
        ) : (
          weekRows.map((row) => (
            <section
              key={row.weekStart}
              className="rounded-lg border border-slate-200 bg-slate-50 p-3"
            >
              <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-sm font-semibold text-slate-900">
                  Week of {formatShortDate(row.weekStart)}
                </h3>
                <span className="text-xs text-slate-500">
                  {(() => {
                    const batchCount = row.days.reduce(
                      (sum, day) => sum + (batchesByDate.get(day)?.length ?? 0),
                      0,
                    );
                    const serializedDevices = row.days.reduce(
                      (sum, day) =>
                        sum + plannedSerializedDevicesForBatches(batchesByDate.get(day) ?? []),
                      0,
                    );
                    const unserializedProducts = row.days.reduce(
                      (sum, day) =>
                        sum + plannedUnserializedProductsForBatches(batchesByDate.get(day) ?? []),
                      0,
                    );
                    return batchAndDeviceSummary(batchCount, serializedDevices, unserializedProducts);
                  })()}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7">
                {row.days.map((day, index) => {
                  const isWeekend = index >= 5;
                  return (
                    <DayCell
                      key={day}
                      date={day}
                      batches={batchesByDate.get(day) ?? []}
                      stationsById={stationsById}
                      hubWritesAllowed={hubWritesAllowed}
                      alternateAvailabilityByBatchId={alternateAvailabilityByBatchId}
                      isToday={day === todayIso}
                      isWeekend={isWeekend}
                    />
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
